/* Bridal Quote Studio™ — "Panoramica" client-detail tab.

  A premium editorial CRM dashboard for the Client Sheet. Mirrors the
  reference image's hero + pills layout but lives BELOW the page's
  existing ClientHero/StatusBar — no duplicate hero (that's already
  rendered by ClientDetail.jsx above the Tabs).

  Read-only by design: cards link/jump to the other tabs for editing.
  This keeps Panoramica fast to render and removes any risk of
  conflicting state with the deep editors in Wedding/Quote/Contract.

  Data flow:
    - All data comes from the parent `client` object (no extra fetch).
    - Cards that have no data hide themselves (`null` return) per the
      brief's "Hide cards if data is missing" requirement.
    - "Documenti rapidi" links open in a new tab; private PDFs (legacy
      quote/contract/timeline) live on /api/clients/:id/*.pdf with
      JWT-via-cookie session, identical to the existing Settings.jsx
      download flow.
*/
import React, { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import CommunicationsWidget from "@/components/CommunicationsWidget";
import {
  CheckCircle2, Circle, FileText, ScrollText, Clock as ClockIcon,
  Wallet, AlertTriangle, Image as ImageIcon, CalendarDays, MapPin,
  Mail, Phone, ChevronRight, ExternalLink, Camera, Heart, Cloud,
  Send, Sparkles, Scissors,
} from "lucide-react";


/* -------------------------------------------------------------------- */
/* UTILITIES                                                            */
/* -------------------------------------------------------------------- */
const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })
    .format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "long", year: "numeric" })
      .format(new Date(iso));
  } catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
};


/* -------------------------------------------------------------------- */
/* TAB ALIAS MAP                                                        */
/* -------------------------------------------------------------------- */
/**
 * Friendly names used inside this component → the actual Radix `Tabs`
 * value attribute on each `<TabsTrigger>` in ClientDetail.jsx. We keep
 * this mapping local to the overview so the rest of the app keeps using
 * the canonical values (`quote`, `wedding-day`, …) while the Panoramica
 * code reads naturally ("preventivo", "wedding_day").
 *
 * Some friendly names target tabs that don't have a 1:1 sibling — those
 * fall back to the closest functional equivalent:
 *   - `documenti` → `quote` (no dedicated documents tab; quote is where
 *     the PDF download lives)
 *   - `note`      → `wedding` (note fields live inside the Wedding tab)
 */
const TAB_ALIAS = {
  preventivo:   "quote",
  wedding_day:  "wedding-day",
  documenti:    "quote",
  note:         "wedding",
  timeline:     "timeline",
  wedding:      "wedding",
  quote:        "quote",
  contract:     "contract",
};


/* -------------------------------------------------------------------- */
/* SECTION CARD — base wrapper                                          */
/* -------------------------------------------------------------------- */
/**
 * @param {function} [onCardClick] When provided, the whole card becomes
 *   an interactive button (cursor pointer, hover lift, keyboard `Enter`).
 *   Skip it for cards that have no single canonical destination
 *   (e.g. Servizi richiesti already has per-row prices and a "Vedi tutti"
 *   CTA is enough).
 */
function SectionCard({ title, subtitle, right, children, testid, onCardClick }) {
  // We previously used a `<button>` wrapper when `onCardClick` was provided,
  // but several cards contain nested `<button>` children (e.g. "Vai a
  // Timeline →") which is invalid HTML and triggered DOM-validation warnings
  // in React. Switching to a `<div role="button">` keeps the same a11y
  // semantics (Enter/Space activation, focus ring) while letting children
  // be real buttons.
  const interactive = !!onCardClick;
  const wrapperProps = interactive
    ? {
        role: "button",
        tabIndex: 0,
        onClick: onCardClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            // Only react to keys when the card itself is focused, never when
            // a nested button (e.g. "Vai a Timeline") forwards the event.
            if (e.currentTarget === e.target) {
              e.preventDefault();
              onCardClick(e);
            }
          }
        },
        className:
          "block w-full text-left bg-white border border-[#EAE3D8] rounded-[18px] " +
          "shadow-[0_1px_2px_rgba(168,139,92,0.04)] overflow-hidden cursor-pointer " +
          "transition-all duration-200 hover:border-[#C8A46A]/60 hover:shadow-[0_3px_12px_rgba(168,139,92,0.08)] " +
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8A46A]/40 focus-visible:ring-offset-2",
      }
    : {
        className:
          "bg-white border border-[#EAE3D8] rounded-[18px] shadow-[0_1px_2px_rgba(168,139,92,0.04)] overflow-hidden",
      };

  return (
    <div {...wrapperProps} data-testid={testid}>
      <div className="px-5 sm:px-6 pt-5 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif-heading text-base sm:text-lg leading-tight text-neutral-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-[#7A746B] mt-0.5">{subtitle}</p>}
        </div>
        {right ? <div className="shrink-0" onClick={(e) => e.stopPropagation()}>{right}</div> : null}
      </div>
      <div className="px-5 sm:px-6 pb-5">{children}</div>
    </div>
  );
}


/* -------------------------------------------------------------------- */
/* 1. MILESTONE TRACKER                                                 */
/* -------------------------------------------------------------------- */
/**
 * Horizontal 7-step progress, computed from the actual client state.
 * Each step has a deterministic completion rule — never a heuristic —
 * so the % is auditable and never drifts from what the editor shows.
 */
function MilestoneTracker({ client }) {
  const q = client.quote || {};
  const c = client.contract || {};
  const trial = (client.makeup?.trial?.date || client.hair?.trial?.date) || "";
  const trialDone = trial && new Date(trial).getTime() < Date.now();
  const balanceZero = (Number(q.balance) || 0) <= 0 && (Number(q.total) || 0) > 0;
  const eventDone = client.wedding_date && new Date(client.wedding_date).getTime() < Date.now();

  const steps = [
    { key: "created",   label: "Cliente creata",       done: true },
    { key: "sent",      label: "Preventivo inviato",   done: ["inviato", "accettato"].includes(q.status) },
    { key: "accepted",  label: "Preventivo accettato", done: q.status === "accettato" },
    { key: "signed",    label: "Contratto firmato",    done: c.status === "firmato" },
    { key: "trial",     label: "Prova effettuata",     done: !!trialDone },
    { key: "paid",      label: "Saldo ricevuto",       done: balanceZero },
    { key: "done",      label: "Evento completato",    done: !!eventDone },
  ];
  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <SectionCard
      title="Stato di avanzamento"
      subtitle="Le tappe principali del percorso con la sposa"
      right={
        <span
          className="text-2xl font-serif-heading text-[#C8A46A] tabular-nums"
          data-testid="overview-progress-pct"
        >
          {pct}%
        </span>
      }
      testid="overview-milestone-card"
    >
      {/* Mobile: vertical list • Desktop: horizontal connector */}
      <div className="mt-2 hidden sm:flex items-start justify-between relative">
        <span
          className="absolute top-3 left-3 right-3 h-px bg-[#EAE3D8] -z-0"
          aria-hidden
        />
        {steps.map((s, i) => (
          <div
            key={s.key}
            className="relative z-10 flex flex-col items-center flex-1 px-1"
            data-testid={`overview-milestone-${s.key}`}
          >
            <span
              className={
                "w-6 h-6 rounded-full flex items-center justify-center shrink-0 " +
                (s.done
                  ? "bg-[#C8A46A] text-white"
                  : "bg-white border border-[#EAE3D8] text-[#C7BFB0]")
              }
            >
              {s.done
                ? <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                : <Circle className="w-3 h-3" strokeWidth={2} />}
            </span>
            <span
              className={
                "mt-2 text-[10px] sm:text-[11px] leading-tight text-center max-w-[80px] " +
                (s.done ? "text-neutral-800 font-medium" : "text-[#7A746B]")
              }
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
      {/* Mobile fallback */}
      <ul className="sm:hidden mt-2 space-y-2">
        {steps.map((s) => (
          <li key={s.key} className="flex items-center gap-2.5 text-xs">
            {s.done
              ? <CheckCircle2 className="w-4 h-4 text-[#C8A46A]" strokeWidth={2} />
              : <Circle className="w-4 h-4 text-[#C7BFB0]" strokeWidth={2} />}
            <span className={s.done ? "text-neutral-800" : "text-[#7A746B]"}>{s.label}</span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* 2. ECONOMIC SUMMARY                                                  */
/* -------------------------------------------------------------------- */
function EconomicSummary({ client, navigate }) {
  const q = client.quote || {};
  const total = Number(q.total) || 0;
  if (total <= 0) return null;

  const depositSum = Array.isArray(q.deposits) && q.deposits.length > 0
    ? q.deposits.reduce((s, d) => s + (Number(d?.amount) || 0), 0)
    : (Number(q.deposit) || 0);
  const balance = Math.max(total - depositSum, 0);
  const paidPct = total > 0 ? Math.round((depositSum / total) * 100) : 0;

  let badgeText = "In attesa";
  let badgeCls = "bg-[#FAF4E5] text-[#A6824A] border-[#EAD9B5]";
  if (balance <= 0) { badgeText = "Saldato"; badgeCls = "bg-[#EEF6EE] text-[#3F7C45] border-[#CFE3CF]"; }
  else if (depositSum > 0) { badgeText = "Acconto ricevuto"; badgeCls = "bg-[#F4EDDF] text-[#6E5530] border-[#E6D9C6]"; }

  const goPreventivo = () => navigate("preventivo");

  return (
    <SectionCard
      title="Riepilogo economico"
      subtitle="Acconti ricevuti e saldo residuo"
      onCardClick={goPreventivo}
      right={
        <Badge variant="outline" className={`text-[10px] tracking-[0.1em] uppercase ${badgeCls}`} data-testid="overview-payment-badge">
          {badgeText}
        </Badge>
      }
      testid="overview-economic-card"
    >
      <dl className="mt-3 space-y-2.5">
        <div className="flex items-baseline justify-between">
          <dt className="text-[11px] uppercase tracking-[0.12em] text-[#7A746B]">Totale preventivo</dt>
          <dd className="font-medium tabular-nums">{fmtEuro(total)}</dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-[11px] uppercase tracking-[0.12em] text-[#7A746B]">Acconto ricevuto</dt>
          <dd className="tabular-nums text-[#3F7C45]" data-testid="overview-deposits-total">+ {fmtEuro(depositSum)}</dd>
        </div>
        <div className="border-t border-[#F0E9DC] pt-2.5 flex items-baseline justify-between">
          <dt className="text-[11px] uppercase tracking-[0.12em] text-neutral-700 font-medium">Saldo residuo</dt>
          <dd className="font-serif-heading text-xl text-neutral-900 tabular-nums" data-testid="overview-balance">{fmtEuro(balance)}</dd>
        </div>
      </dl>
      {/* Slim progress bar */}
      <div className="mt-4 h-1 bg-[#F0E9DC] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#C8A46A] transition-all"
          style={{ width: `${Math.min(paidPct, 100)}%` }}
        />
      </div>
      <p className="text-[10px] text-[#7A746B] mt-2 text-right">{paidPct}% incassato</p>
      <span
        className="mt-3 text-[11px] inline-flex items-center gap-1 text-[#7A6541] group-hover:text-[#A6824A] transition-colors pointer-events-none"
        data-testid="overview-economic-jump"
      >
        Vai al preventivo
        <ChevronRight className="w-3 h-3" strokeWidth={1.6} />
      </span>
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* 3. UPCOMING APPOINTMENTS                                             */
/* -------------------------------------------------------------------- */
function UpcomingAppointments({ client, navigate }) {
  const items = useMemo(() => {
    const out = [];
    if (client.wedding_date) {
      out.push({
        key: "wedding",
        icon: Heart,
        label: "Matrimonio",
        date: client.wedding_date,
        time: client.ceremony_time || "",
        sub: client.prep_venue || "",
      });
    }
    if (client.makeup?.trial?.date) {
      out.push({
        key: "makeup-trial",
        icon: Sparkles,
        label: "Prova Makeup",
        date: client.makeup.trial.date,
        time: client.makeup.trial.time || "",
        sub: client.makeup.trial.location || "",
      });
    }
    if (client.hair?.trial?.date) {
      out.push({
        key: "hair-trial",
        icon: Scissors,
        label: "Prova Hair",
        date: client.hair.trial.date,
        time: client.hair.trial.time || "",
        sub: client.hair.trial.location || "",
      });
    }
    return out
      .map(it => ({ ...it, _t: new Date(it.date).getTime() }))
      .filter(it => !Number.isNaN(it._t) && it._t >= Date.now() - 86_400_000)
      .sort((a, b) => a._t - b._t)
      .slice(0, 4);
  }, [client]);

  if (items.length === 0) return null;

  return (
    <SectionCard
      title="Prossimi appuntamenti"
      subtitle="Le date che ti aspettano"
      right={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate("timeline"); }}
          className="text-[11px] text-[#7A6541] hover:text-[#A6824A] inline-flex items-center gap-1"
          data-testid="overview-appointments-jump"
        >
          Vedi tutti <ChevronRight className="w-3 h-3" strokeWidth={1.6} />
        </button>
      }
      testid="overview-appointments-card"
    >
      <ul className="mt-3 space-y-3">
        {items.map((it) => {
          const Icon = it.icon;
          const d = daysUntil(it.date);
          return (
            <li
              key={it.key}
              className="flex items-start gap-3 group"
              data-testid={`overview-appt-${it.key}`}
            >
              <span className="w-9 h-9 rounded-full bg-[#F4EDDF] border border-[#E6D9C6] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#A6824A]" strokeWidth={1.6} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-medium text-neutral-900">{it.label}</span>
                  {d !== null && d >= 0 && (
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[#A6824A]">
                      {d === 0 ? "oggi" : d === 1 ? "domani" : `tra ${d} giorni`}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#7A746B] mt-0.5">
                  {fmtDate(it.date)}{it.time ? ` · ${it.time}` : ""}{it.sub ? ` · ${it.sub}` : ""}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* 4. WEDDING DAY SNAPSHOT                                              */
/* -------------------------------------------------------------------- */
/**
 * Minimal teaser — real wedding-day routing/weather lives in the
 * Wedding Day tab. We surface the headline values + a deep link.
 * No fetch: just shows what's already on the client document.
 */
function WeddingDaySnapshot({ client, navigate }) {
  if (!client.wedding_date) return null;
  const d = daysUntil(client.wedding_date);
  const goWeddingDay = () => navigate("wedding_day");
  return (
    <SectionCard
      title="Wedding Day"
      subtitle="Anteprima della giornata"
      onCardClick={goWeddingDay}
      right={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goWeddingDay(); }}
          className="text-[11px] text-[#7A6541] hover:text-[#A6824A] inline-flex items-center gap-1"
          data-testid="overview-weddingday-jump"
        >
          Apri <ChevronRight className="w-3 h-3" strokeWidth={1.6} />
        </button>
      }
      testid="overview-weddingday-card"
    >
      <div className="mt-2 grid grid-cols-3 gap-3">
        <div className="text-center bg-[#FAF8F4] rounded-[14px] py-3 border border-[#F0E9DC]">
          <CalendarDays className="w-4 h-4 mx-auto text-[#A6824A]" strokeWidth={1.6} />
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#7A746B] mt-1.5">Conto alla rovescia</p>
          <p className="font-serif-heading text-lg mt-0.5 tabular-nums">
            {d !== null ? (d >= 0 ? `${d}gg` : "—") : "—"}
          </p>
        </div>
        <div className="text-center bg-[#FAF8F4] rounded-[14px] py-3 border border-[#F0E9DC]">
          <ClockIcon className="w-4 h-4 mx-auto text-[#A6824A]" strokeWidth={1.6} />
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#7A746B] mt-1.5">Inizio lavoro</p>
          <p className="font-serif-heading text-lg mt-0.5 tabular-nums">{client.work_start_time || "—"}</p>
        </div>
        <div className="text-center bg-[#FAF8F4] rounded-[14px] py-3 border border-[#F0E9DC]">
          <Heart className="w-4 h-4 mx-auto text-[#A6824A]" strokeWidth={1.6} />
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#7A746B] mt-1.5">Cerimonia</p>
          <p className="font-serif-heading text-lg mt-0.5 tabular-nums">{client.ceremony_time || "—"}</p>
        </div>
      </div>
      {client.prep_venue && (
        <p className="text-[11px] text-[#7A746B] mt-3 flex items-start gap-1.5">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.6} />
          <span className="truncate">{client.prep_venue}</span>
        </p>
      )}
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* 5. IMPORTANT NOTES                                                   */
/* -------------------------------------------------------------------- */
function ImportantNotes({ client, navigate }) {
  const notes = (client.notes || "").trim();
  const eventNotes = (client.event_notes || "").trim();
  if (!notes && !eventNotes) return null;
  const goNote = () => navigate("note");
  return (
    <SectionCard
      title="Note importanti"
      subtitle="Da ricordare prima dell'evento"
      onCardClick={goNote}
      right={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNote(); }}
          className="text-[11px] text-[#7A6541] hover:text-[#A6824A] inline-flex items-center gap-1"
          data-testid="overview-notes-jump"
        >
          Modifica note <ChevronRight className="w-3 h-3" strokeWidth={1.6} />
        </button>
      }
      testid="overview-notes-card"
    >
      <div className="mt-2 space-y-3">
        {notes && (
          <div className="bg-[#FFF8EC] border border-[#F0DFB5] rounded-[14px] px-4 py-3 flex gap-2.5">
            <AlertTriangle className="w-4 h-4 text-[#B58D3E] shrink-0 mt-0.5" strokeWidth={1.6} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[#9A7434] font-medium">Note generali</p>
              <p className="text-xs text-neutral-700 mt-1 whitespace-pre-wrap leading-relaxed">{notes}</p>
            </div>
          </div>
        )}
        {eventNotes && (
          <div className="bg-[#FAF8F4] border border-[#EAE3D8] rounded-[14px] px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.12em] text-[#7A746B] font-medium">Note evento</p>
            <p className="text-xs text-neutral-700 mt-1 whitespace-pre-wrap leading-relaxed">{eventNotes}</p>
          </div>
        )}
      </div>
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* 6. REQUESTED SERVICES                                                */
/* -------------------------------------------------------------------- */
function RequestedServices({ client, navigate }) {
  const items = (client.quote?.items || []).filter((it) => {
    if (!it) return false;
    if (it.is_optional) {
      const decision = it.client_decision || (it.client_selected ? "accepted" : "pending");
      return decision === "accepted";
    }
    return true;
  });
  if (items.length === 0) return null;
  const display = items.slice(0, 6);
  const extra = items.length - display.length;
  const goWedding = () => navigate("wedding");

  return (
    <SectionCard
      title="Servizi richiesti"
      subtitle={`${items.length} ${items.length === 1 ? "voce confermata" : "voci confermate"} sul preventivo`}
      onCardClick={goWedding}
      right={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goWedding(); }}
          className="text-[11px] text-[#7A6541] hover:text-[#A6824A] inline-flex items-center gap-1"
          data-testid="overview-services-jump"
        >
          Vedi tutti <ChevronRight className="w-3 h-3" strokeWidth={1.6} />
        </button>
      }
      testid="overview-services-card"
    >
      <ul className="mt-2 space-y-1.5">
        {display.map((it, i) => (
          <li
            key={i}
            className="flex items-baseline justify-between gap-3 py-1.5 border-b border-[#F0E9DC] last:border-0"
            data-testid={`overview-service-${i}`}
          >
            <span className="text-sm text-neutral-800 truncate flex-1">
              {it.name || it.description || "Servizio"}
              {Number(it.qty) > 1 && <span className="text-[10px] text-[#7A746B] ml-1.5">× {it.qty}</span>}
            </span>
            <span className="text-xs text-[#7A746B] tabular-nums shrink-0">
              {fmtEuro((Number(it.price) || 0) * (Number(it.qty) || 0) * (1 - (Number(it.discount) || 0) / 100))}
            </span>
          </li>
        ))}
      </ul>
      {extra > 0 && (
        <p className="text-[11px] text-[#7A746B] mt-3 italic">+ altri {extra} servizi</p>
      )}
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* 7. QUICK DOCUMENTS                                                   */
/* -------------------------------------------------------------------- */
/**
 * Each "document" is a shortcut to the tab where the actual download
 * lives (it's behind JWT-cookie auth + uses axios, so we can't use a
 * plain anchor). The public-bride link is the only true direct URL.
 */
function QuickDocuments({ client, clientShareToken, navigate }) {
  const docs = useMemo(() => {
    const list = [];
    if (client.quote && (Number(client.quote.total) || 0) > 0) {
      list.push({
        key: "quote",
        label: "Preventivo PDF",
        icon: FileText,
        onClick: () => navigate("preventivo"),
      });
    }
    if (client.contract && client.contract.status && client.contract.status !== "non_inviato") {
      list.push({
        key: "contract",
        label: "Contratto PDF",
        icon: ScrollText,
        onClick: () => navigate("contract"),
      });
    }
    const hasTimeline = Array.isArray(client.timeline) && client.timeline.length > 0;
    if (hasTimeline) {
      list.push({
        key: "timeline",
        label: "Timeline PDF",
        icon: ClockIcon,
        onClick: () => navigate("timeline"),
      });
    }
    if (clientShareToken) {
      list.push({
        key: "public",
        label: "Link pubblico sposa",
        icon: ExternalLink,
        href: `${window.location.origin}/cliente/${clientShareToken}`,
        external: true,
      });
    }
    return list;
  }, [client, clientShareToken, navigate]);

  if (docs.length === 0) return null;
  const goDocs = () => navigate("documenti");

  return (
    <SectionCard
      title="Documenti rapidi"
      subtitle="Scarica o condividi con un click"
      onCardClick={goDocs}
      right={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goDocs(); }}
          className="text-[11px] text-[#7A6541] hover:text-[#A6824A] inline-flex items-center gap-1"
          data-testid="overview-docs-jump"
        >
          Vai alla sezione documenti <ChevronRight className="w-3 h-3" strokeWidth={1.6} />
        </button>
      }
      testid="overview-docs-card"
    >
      <div
        className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {docs.map((d) => {
          const Icon = d.icon;
          const cls =
            "flex items-center gap-3 px-3.5 py-3 rounded-[14px] border border-[#EAE3D8] bg-[#FAF8F4] " +
            "hover:bg-white hover:border-[#C8A46A]/40 transition-colors group text-left w-full cursor-pointer";
          const inner = (
            <>
              <span className="w-9 h-9 rounded-full bg-white border border-[#EAE3D8] flex items-center justify-center shrink-0 group-hover:border-[#C8A46A]/50 transition-colors">
                <Icon className="w-4 h-4 text-[#A6824A]" strokeWidth={1.6} />
              </span>
              <span className="text-sm text-neutral-800 truncate flex-1">{d.label}</span>
              <ChevronRight className="w-4 h-4 text-[#C7BFB0] group-hover:text-[#A6824A] shrink-0" strokeWidth={1.6} />
            </>
          );
          return d.href ? (
            <a
              key={d.key}
              href={d.href}
              target={d.external ? "_blank" : "_self"}
              rel={d.external ? "noopener noreferrer" : undefined}
              className={cls}
              data-testid={`overview-doc-${d.key}`}
            >{inner}</a>
          ) : (
            <button
              key={d.key}
              type="button"
              onClick={d.onClick}
              className={cls}
              data-testid={`overview-doc-${d.key}`}
            >{inner}</button>
          );
        })}
      </div>
    </SectionCard>
  );
}


/* -------------------------------------------------------------------- */
/* MAIN — orchestrates the responsive grid                              */
/* -------------------------------------------------------------------- */
export default function ClientOverview({ client, hasMakeup, hasHair, clientShareToken, onNavigate, onResendQuote }) {
  if (!client) return null;
  /**
   * Translates a friendly target name ("preventivo", "wedding_day", …)
   * into the actual Radix tab `value` and tells the parent to switch.
   * Wraps the navigation in a single chokepoint so the alias map stays
   * authoritative and so every CTA gets identical behaviour (no double
   * fire, no stale ref). The `onNavigate` parent setter is the
   * controlled-`<Tabs value>` state setter on ClientDetail.
   */
  const navigate = (friendly) => {
    if (!onNavigate) return;
    const target = TAB_ALIAS[friendly] || friendly;
    onNavigate(target);
    // Scroll the page back to the top of the tab list so the destination
    // tab is in view (helpful on mobile / when the user scrolled deep
    // into the Panoramica cards).
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { /* old browsers */ }
  };

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="client-overview-grid">
      {/* ROW 1: Milestone tracker (wide) + Economic summary (narrow) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
        <div className="lg:col-span-2">
          <MilestoneTracker client={client} />
        </div>
        <div>
          <EconomicSummary client={client} navigate={navigate} />
        </div>
      </div>

      {/* ROW 2: Appointments + Wedding Day snapshot + Important notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 items-start">
        <UpcomingAppointments client={client} navigate={navigate} />
        <WeddingDaySnapshot client={client} navigate={navigate} />
        <ImportantNotes client={client} navigate={navigate} />
      </div>

      {/* ROW 3: Requested services (wide) + Quick documents (narrow) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 items-start">
        <div className="lg:col-span-2">
          <RequestedServices client={client} navigate={navigate} />
        </div>
        <div>
          <QuickDocuments client={client} clientShareToken={clientShareToken} navigate={navigate} />
        </div>
      </div>

      {/* ROW 4: Communications tracking — only renders if the quote/contract
          has actually been sent (the widget hides itself otherwise). */}
      <CommunicationsWidget
        client={client}
        onNavigate={(friendly) => navigate(friendly)}
        onResend={onResendQuote}
      />
    </div>
  );
}
